import type { ButtonProps } from '@mui/material';
import { styled, Button } from '@mui/material';
import React, { forwardRef } from 'react';

// A theme without the radius tokens (a bare MUI theme) keeps the circle.
const toCssLength = (value: number | string | undefined) =>
  value === undefined
    ? '50%'
    : typeof value === 'number'
      ? `${value}px`
      : value;

const IconButtonWithButtonStyle = styled(Button)<{
  size: string;
}>`
  && {
    min-width: unset;
    border-radius: ${({ theme }) => toCssLength(theme.buttonRadius?.main)};
    ${({ theme }) => theme.breakpoints.down('sm')} {
      border-radius: ${({ theme }) => toCssLength(theme.buttonRadius?.mobile)};
    }
    ${({ size }) => {
      switch (size) {
        case 'small':
          return `
          padding: 8px;
          width: 32px;
          height: 32px;
          & .MuiSvgIcon-root, & svg {
            font-size: 16px;
            width: 16px;
            height: 16px;
          }
        `;
        case 'large':
          return `
          padding: 12px;
          width: 48px;
          height: 48px;
          & .MuiSvgIcon-root, & svg {
            font-size: 24px;
            width: 24px;
            height: 24px;
          }
        `;
        case 'medium':
        default:
          return `
          padding: 10px;
          width: 40px;
          height: 40px;
          & .MuiSvgIcon-root, & svg {
            font-size: 20px;
            width: 20px;
            height: 20px;
          }
        `;
      }
    }}
  }
`;

interface TixlyIconButtonProps extends ButtonProps {
  children: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
}

export const TixlyIconButton = forwardRef<
  HTMLButtonElement,
  TixlyIconButtonProps
>(function TixlyIconButton(
  {
    children,
    color = 'primary',
    size = 'medium',
    variant = 'contained',
    ...rest
  },
  ref
) {
  return (
    <IconButtonWithButtonStyle
      variant={variant}
      color={color}
      size={size}
      ref={ref}
      {...rest}
    >
      {children}
    </IconButtonWithButtonStyle>
  );
});
